import { useStores, type Journal } from '@littlebird/sdk';
import { useNavigate, useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Empty } from '@/components/ui/EmptyState';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Skeleton } from '@/components/ui/skeleton';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { useScrollParentSentinelLoadMore } from '@/hooks/use-scroll-parent-sentinel-load-more';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/date';

import { journalApis, toJournalDisplayDate } from './journal.apis';
import {
  type InsufficientContextEntry,
  isInsufficientContextEntry,
} from './journal.types';

export const JournalHistoryList = observer(
  ({ className }: { className?: string }) => {
    const { journals: journalStore } = useStores();
    const filteredJournals = journalApis.useFilteredJournals();
    const isLoading = journalStore.isFetching;

    const { sentinelRef, isLoadingMore } = useScrollParentSentinelLoadMore({
      hasMorePages: journalStore.hasMorePages,
      loadNextPage: () => journalStore.loadNextPage(),
      enabled:
        !isLoading &&
        journalStore.orderedData.length > 0 &&
        filteredJournals.length > 0,
    });

    if (isLoading) return <JournalHistoryLoading />;

    const noJournals = journalStore.orderedData.length === 0;

    if (noJournals) {
      return null;
    }

    if (filteredJournals.length === 0) {
      return (
        <Empty.Root variant="compact">
          <Empty.Header>
            <Empty.Description>
              No journals match the selected filter.
            </Empty.Description>
          </Empty.Header>
        </Empty.Root>
      );
    }

    return (
      <ScrollMask
        className={cn(
          'flex flex-col gap-2',
          'w-full max-w-56',
          'h-full',
          'scrollbar-hide',
          className
        )}
      >
        {filteredJournals.map((item) =>
          isInsufficientContextEntry(item) ? (
            <InsufficientContextHistoryItem key={item.id} entry={item} />
          ) : (
            <JournalHistoryItem key={item.id} journal={item} />
          )
        )}
        {journalStore.hasMorePages && (
          <div ref={sentinelRef} className="h-2 w-full shrink-0" aria-hidden />
        )}
        {isLoadingMore && (
          <p className="text-center text-xs text-foreground-muted py-2">
            Loading more...
          </p>
        )}
      </ScrollMask>
    );
  }
);

const JournalHistoryLoading = () => {
  return (
    <YStack className="w-full max-w-56 h-full gap-2">
      {Array.from({ length: 8 }).map((_, index) => (
        <YStack
          key={index}
          className="gap-1 px-3 py-2 rounded-md border border-border"
        >
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-full" />
        </YStack>
      ))}
    </YStack>
  );
};

type JournalHistoryItemProps = {
  journal: Journal;
};

const JournalHistoryItem = observer(({ journal }: JournalHistoryItemProps) => {
  const { journalId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { openTab } = useTabs();

  const isSelected = journalId === journal.id;
  const journalDate = toJournalDisplayDate(journal.date);
  const isToday = journalDate.toDateString() === new Date().toDateString();

  const dateString = formatDate(journalDate, 'MMM do');
  const title = journal.sections?.title || (isToday ? 'Today' : dateString);
  const isUnread = !journal.viewedAt;

  return (
    <YStack
      onClick={(e: React.MouseEvent) => {
        if (isNewTabClick(e)) {
          openTab({ path: `/journal/${journal.id}` });
        } else {
          navigate({
            to: '/journal/$journalId',
            params: { journalId: journal.id },
          });
        }
      }}
      className={cn(
        'gap-1 px-3 py-2 rounded-lg border border-border',
        isSelected ? 'bg-surface' : 'border-transparent hover:bg-surface-hover'
      )}
    >
      <p
        className={cn(
          'text-xs font-medium',
          isSelected ? 'text-foreground' : 'text-foreground-subtle',
          isUnread && 'text-foreground font-semibold'
        )}
      >
        {dateString}
      </p>
      <XStack className="items-center gap-1">
        <p
          className={cn(
            'text-xs',
            'truncate',
            'text-foreground-muted',
            isUnread && 'text-foreground'
          )}
        >
          {title}
        </p>
      </XStack>
    </YStack>
  );
});

type InsufficientContextHistoryItemProps = {
  entry: InsufficientContextEntry;
};

const InsufficientContextHistoryItem = ({
  entry,
}: InsufficientContextHistoryItemProps) => {
  const { journalId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { openTab } = useTabs();

  const isSelected = journalId === entry.id;
  const { dayCount, startDate, endDate } = entry;

  const handleClick = (e: React.MouseEvent) => {
    if (isNewTabClick(e)) {
      openTab({ path: `/journal/${entry.id}` });
    } else {
      navigate({ to: '/journal/$journalId', params: { journalId: entry.id } });
    }
  };

  const formatDateRange = () => {
    if (dayCount === 1) {
      return formatDate(startDate, 'MMM do');
    }
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${formatDate(startDate, 'MMM do')} - ${formatDate(
        endDate,
        'do'
      )}`;
    }
    return `${formatDate(startDate, 'MMM do')} - ${formatDate(
      endDate,
      'MMM do'
    )}`;
  };

  return (
    <YStack
      onClick={handleClick}
      className={cn(
        'gap-1 px-3 py-2 rounded-lg border',
        isSelected
          ? 'bg-surface border-border'
          : 'border-transparent hover:bg-surface-hover'
      )}
    >
      <XStack className="items-center gap-1.5">
        <p
          className={cn(
            'text-xs font-medium',
            isSelected ? 'text-foreground' : 'text-foreground-subtle'
          )}
        >
          {formatDateRange()}
        </p>
      </XStack>
      <XStack className="gap-1.5 items-center">
        <Icons.noteError className="size-2.5 text-foreground-muted flex-shrink-0" />
        <p className="text-xs text-foreground-muted">
          {dayCount === 1
            ? 'Insufficient context'
            : `Insufficient context for ${dayCount} days`}
        </p>
      </XStack>
    </YStack>
  );
};
